import React, { useState, useEffect } from 'react';
import { ArrowRightCircle, BedDouble } from 'lucide-react';

export default function TransferModal({ show, onClose, onConfirm, reservation, beds = [], sectors = [] }) {
    const [targetSectorId, setTargetSectorId] = useState('');
    const [targetBedId, setTargetBedId] = useState('');
    const [observation, setObservation] = useState('');
    const [loading, setLoading] = useState(false);

    // Reset state when modal opens
    useEffect(() => {
        if (show) {
            setTargetSectorId('');
            setTargetBedId('');
            setObservation('');
        }
    }, [show]);

    if (!show || !reservation) return null;

    // Filter available beds based on selected sector
    const availableBeds = beds.filter(b =>
        (targetSectorId === '' || b.sectorId.toString() === targetSectorId) &&
        (b.statusId === 1 || b.statusId === 9) // 1=Livre, 9=Higienizado
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!targetBedId) return alert('Selecione um leito de destino.');

        setLoading(true);
        await onConfirm({
            reservationId: reservation.id,
            targetSectorId,
            targetBedId, // This is critical for the transfer logic
            observation
        });
        setLoading(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-800 flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="p-6 border-b border-gray-100 dark:border-gray-800 bg-indigo-50/50 dark:bg-gray-900 rounded-t-2xl">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                            <ArrowRightCircle size={24} />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                Transferência Interna
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Movimentação de paciente entre leitos/setores
                            </p>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto">

                    {/* Patient Summary */}
                    <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Paciente</span>
                            <span className="badge bg-indigo-100 text-indigo-700">{reservation.origin}</span>
                        </div>
                        <h4 className="font-bold text-gray-800 dark:text-white text-lg">{reservation.patient_name}</h4>
                        <p className="text-sm text-gray-500">{reservation.patient_gender}, {reservation.patient_condition}</p>
                    </div>

                    {/* Step 1: Select Sector */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            1. Setor de Destino
                        </label>
                        <select
                            className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                            value={targetSectorId}
                            onChange={(e) => {
                                setTargetSectorId(e.target.value);
                                setTargetBedId(''); // Reset bed when sector changes
                            }}
                            required
                        >
                            <option value="">Selecione o setor...</option>
                            {sectors.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Step 2: Select Bed */}
                    <div className={`transition-all duration-300 ${!targetSectorId ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            2. Leito de Destino
                        </label>
                        <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2">
                            {availableBeds.length > 0 ? (
                                availableBeds.map(bed => (
                                    <label key={bed.id} className="flex items-center gap-3 p-3 border border-gray-100 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors has-[:checked]:border-indigo-500 has-[:checked]:bg-indigo-50 dark:has-[:checked]:bg-indigo-900/20">
                                        <input
                                            type="radio"
                                            name="targetBed"
                                            value={bed.id}
                                            checked={targetBedId == bed.id}
                                            onChange={(e) => setTargetBedId(e.target.value)}
                                            className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                        />
                                        <div className="flex-1 flex justify-between items-center">
                                            <span className="font-semibold text-gray-700 dark:text-gray-200">Leito {bed.number}</span>
                                            <span className={`text-xs px-2 py-1 rounded-full ${bed.statusId === 1 ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                                {bed.statusId === 1 ? 'Livre' : 'Higienizado'}
                                            </span>
                                        </div>
                                    </label>
                                ))
                            ) : (
                                <div className="text-center py-4 text-gray-400 text-sm">
                                    Nenhum leito disponível neste setor.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Observation */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Observação
                        </label>
                        <textarea
                            value={observation}
                            onChange={(e) => setObservation(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-black/20 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            rows="2"
                            placeholder="Motivo da transferência..."
                        ></textarea>
                    </div>

                    {/* Footer Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 rounded-lg transition-colors font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !targetBedId}
                            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg shadow-sm hover:shadow transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Processando...' : 'Confirmar Transferência'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
